import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { Topbar } from "../components/shell/Topbar";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/button";
import { StatusBadge, statusToTone } from "../components/shell/StatusBadge";
import { ReleaseGenerateDialog } from "../components/release/ReleaseGenerateDialog";

interface Release {
  id: string;
  title: string;
  content: string;
  status: string;
  publishedAt: string | null;
  fromDate: string;
  toDate: string;
  commitCount: number;
  errorMessage?: string | null;
  createdAt: string;
}

export function ReleasesListPage() {
  const { teamId, projectId } = useParams();
  const { teams, projects } = useLayoutContext();
  const team = teams.find((t) => t.id === teamId);
  const project = (projects[teamId ?? ""] ?? []).find((p) => p.referenceId === projectId);
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);

  const refresh = (showLoading = false) => {
    if (!projectId) return;
    if (showLoading) setLoading(true);
    fetch(`/api/projects/${projectId}/releases`)
      .then((r) => r.json())
      .then((d) => {
        setReleases(d);
        setLoading(false);
      });
  };

  useEffect(() => {
    refresh(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const hasProcessing = releases.some((r) => r.status === "processing");
  useEffect(() => {
    if (!projectId || !hasProcessing) return;
    const interval = setInterval(() => refresh(), 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, hasProcessing]);

  if (!team || !project) return null;

  return (
    <>
      <Topbar
        title="Releases"
        crumbs={[
          { label: team.name, to: `/teams/${team.id}` },
          {
            label: project.name,
            to: `/teams/${team.id}/projects/${project.referenceId}`,
          },
          { label: "Releases" },
        ]}
        actions={
          <>
            <Link
              to={`/teams/${teamId}/projects/${projectId}/releases/settings`}
            >
              <Button size="sm" variant="outline">
                Settings
              </Button>
            </Link>
            <Button size="sm" onClick={() => setShowGenerate(true)}>
              + New release
            </Button>
          </>
        }
      />

      <div className="px-4 sm:px-8 py-6">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : releases.length === 0 ? (
          <div className="bg-panel/40 border border-dashed border-rule rounded-xl p-8 text-center">
            <div className="font-serif text-xl font-semibold mb-2">
              No releases yet
            </div>
            <div className="text-sm text-muted-foreground mb-4">
              Connect a GitHub repo and generate your first release from
              commits.
            </div>
            <Button onClick={() => setShowGenerate(true)}>Generate first release</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {releases.map((r) => (
              <Link
                key={r.id}
                to={`/teams/${teamId}/projects/${projectId}/releases/${r.id}`}
                className="bg-card-paper border border-rule rounded-xl p-4 hover:shadow-lg transition-all hover:-translate-y-px"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-mono text-[10px] text-faint">
                    {format(new Date(r.createdAt), "MMMM do 'of' yyyy")}
                  </span>
                  <StatusBadge tone={statusToTone(r.status)}>
                    {r.status}
                  </StatusBadge>
                </div>
                <div className="font-serif text-lg font-semibold tracking-tight mb-2 line-clamp-2 min-h-[2.4em]">
                  {r.title || "Untitled release"}
                </div>
                <div className="text-xs text-muted-foreground line-clamp-3 mb-3 min-h-[3em]">
                  {r.content?.slice(0, 220) || (
                    <span className="italic">
                      {r.errorMessage ?? "Generating release notes…"}
                    </span>
                  )}
                </div>
                <div className="flex justify-between items-center font-mono text-[10px] text-faint">
                  <span>{r.commitCount} commits</span>
                  <span className="text-signal-red font-semibold">Open →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <ReleaseGenerateDialog
        open={showGenerate}
        onOpenChange={setShowGenerate}
        projectId={projectId!}
        onGenerated={refresh}
      />
    </>
  );
}
